import React, { useEffect, useState } from 'react';

import { useGetCardsCount, useGetCards } from '@vlabs/api/artemy/client';
import type { CardId, GetCardsParams } from '@vlabs/api/artemy/model';
import { SortCardsDropdown } from '@vlabs/monokit/entities/cards';
import { CardListFastFiltersPoint3 } from '@vlabs/monokit/entities/cards/filters';
import { useCardsFilters } from '@vlabs/monokit/entities/cards/filters/useCardsFilters';
import { useWildcardCardListFiltersValue } from '@vlabs/monokit/entities/cards/filters/useWildcardFiltersValue';
import { PersonCard } from '@vlabs/monokit/shared/ui/person-card/PersonCard.point3';
import { useHasPermission } from '@vlabs/permissions';
import { PaginationPanel } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import { CardsBulkActions } from './bulk-actions';
import st from './ListDetailsViewer.module.sass';

export function ListCardsViewer(): React.JSX.Element {
  const { t } = useTranslation();
  const [selectedCardIds, setSelectedCardIds] = useState<CardId[]>([]);
  const { filters, updateFilters } = useCardsFilters();
  const wildCardFilters = useWildcardCardListFiltersValue();
  const getCards = useGetCards(wildCardFilters as GetCardsParams);
  const getCardCount = useGetCardsCount(wildCardFilters);

  const canUpdateCard = useHasPermission('card', 'update');
  const canDeleteCard = useHasPermission('card', 'delete');
  const canBulkOperate = canUpdateCard || canDeleteCard;

  useEffect(() => setSelectedCardIds([]), [getCards.data]);

  const cards = getCards?.data?.cards ?? [];
  const cardsTotalCount = getCardCount?.data?.cards_count ?? 0;
  const selectedCount = selectedCardIds.length;

  const isBulkMode = selectedCount > 0;
  const isAllSelected = cards.length > 0 && selectedCount === cards.length;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  const handleToggleAll = () => {
    if (isAllSelected) {
      setSelectedCardIds([]);
      return;
    }

    setSelectedCardIds(cards.map((card) => card.card_id as CardId));
  };

  const handleToggleCard = (externalId: string): void => {
    setSelectedCardIds((prev) => (
      prev.includes(externalId)
        ? prev.filter((id) => id !== externalId)
        : [...prev, externalId]
    ));
  };

  return (
    <div className={st.Root}>
      {!isBulkMode && (
        <div className={st.Toolbar}>
          <div className={st.ToolbarContent}>
            <div className={st.Count}>
              {t('list-details-page:titles.quantity', { context: 'cards', count: cardsTotalCount })}
            </div>

            <CardListFastFiltersPoint3 exclude={['list_ids']} />
          </div>

          <SortCardsDropdown />
        </div>
      )}

      {!!canBulkOperate && !!isBulkMode && (
        <div className={st.BulkActions}>
          <CardsBulkActions
            indeterminate={isIndeterminate}
            onCancel={() => setSelectedCardIds([])}
            onCheckboxClick={handleToggleAll}
            selectedCardIds={selectedCardIds}
          />
        </div>
      )}

      <div className={st.Content}>
        <div className={st.GridList}>
          {cards?.map((card) => {
            let onSelect;
            if (canBulkOperate) {
              onSelect = () => handleToggleCard(card.card_id);
            }
            return (
              <PersonCard
                card={card}
                isSelected={selectedCardIds.includes(card.card_id)}
                key={card.card_id}
                onSelect={onSelect}
                size="small"
              />
            );
          })}
        </div>
      </div>
      <div className={st.Pagination}>
        <PaginationPanel
          canNextPage={(getCards?.data?.cards?.length ?? 0) >= (filters.page_size ?? 0)}
          canPreviousPage={(filters?.page ?? 0) > 0}
          gotoPage={(page) => updateFilters({ page })}
          nextPage={() => updateFilters({ page: (filters?.page ?? 0) + 1 })}
          pageCount={Math.floor(((getCardCount?.data?.cards_count ?? 0) / (filters?.page_size ?? 25)))}
          pageIndex={filters.page}
          pageSize={filters.page_size}
          previousPage={() => updateFilters({ page: (filters?.page ?? 0) - 1 })}
          setPageSize={(page_size: number) => updateFilters({ page_size })}
        />
      </div>
    </div>
  );
}
